import { useEditPanelViewport } from '../../ui/use-edit-panel-viewport.js';
import * as Dialog from '@radix-ui/react-dialog';
import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useDianaUIPortal } from '../../ui/index.js';
import { AdminApiError } from '../../lib/api-client.js';
import type { ShootDraft } from './shoot-conflict.js';
import { financeSummary, parseTransactionAmount, todayInLisbon } from './shoot-finance.js';
import { formatCrmMoney, formatCrmMoneyInput } from './crm-money.js';
import './ShootMoney.css';

type Pending = {
  draft: ShootDraft;
  id: string;
  remaining: number;
  commit: (draft: ShootDraft) => Promise<boolean>;
  resolve: (value: boolean) => void;
  reject: (reason: unknown) => void;
};
export type EditingPaymentRequest = (
  draft: ShootDraft,
  commit: (draft: ShootDraft) => Promise<boolean>,
) => Promise<boolean>;
export function useEditingPayment() {
  const portal = useDianaUIPortal();
  const current = useRef<Pending | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const lastPending = useRef<Pending | null>(null);
  if (pending) lastPending.current = pending;
  const displayPending = pending ?? lastPending.current;
  const { panelRef, bodyRef } = useEditPanelViewport(pending !== null);
  const content = useRef<HTMLDivElement | null>(null);
  const capturePanel = useCallback(
    (node: HTMLDivElement | null) => {
      content.current = node;
      panelRef(node);
    },
    [panelRef],
  );
  const [amount, setAmount] = useState('');
  const [galleryUrl, setGalleryUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const finish = useCallback((value: boolean) => {
    const previous = current.current;
    current.current = null;
    setPending(null);
    setSaving(false);
    setError('');
    previous?.resolve(value);
  }, []);
  useEffect(
    () => () => {
      current.current?.resolve(false);
      current.current = null;
    },
    [],
  );
  const request: EditingPaymentRequest = (draft, commit) => {
    const remaining = financeSummary(draft.priceAmountMinor, draft.transactions ?? []).remaining;
    if (draft.status !== 'Delivered' && (remaining === null || remaining <= 0))
      return commit(draft);
    if (current.current) return Promise.resolve(false);
    returnFocus.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return new Promise((resolve, reject) => {
      const value = {
        draft: structuredClone(draft),
        remaining: remaining ?? 0,
        id: crypto.randomUUID(),
        commit,
        resolve,
        reject,
      };
      current.current = value;
      setPending(value);
      setAmount(formatCrmMoneyInput(remaining ?? 0));
      setGalleryUrl(draft.galleryUrl ?? '');
      setSaving(false);
      setError('');
    });
  };
  const submit = async (record: boolean) => {
    const value = current.current;
    if (!value || saving) return;
    const delivery = value.draft.status === 'Delivered';
    let link = galleryUrl.trim();
    if (delivery) {
      try {
        const url = new URL(link);
        if (!['http:', 'https:'].includes(url.protocol) || link.length > 2000) throw new Error();
      } catch {
        setError('Enter a valid gallery link (https://…).');
        return;
      }
    }
    const amountMinor = record ? parseTransactionAmount(amount) : null;
    if (record && !amountMinor) {
      setError('Enter an amount greater than zero.');
      return;
    }
    const transactions = [...(value.draft.transactions ?? [])];
    if (record)
      transactions.push({
        id: value.id,
        kind: 'Income',
        amountMinor: amountMinor!,
        date: todayInLisbon(),
        description: '',
        purpose: 'Balance',
      });
    setSaving(true);
    setError('');
    try {
      if (
        await value.commit({
          ...value.draft,
          transactions,
          ...(delivery ? { galleryUrl: link } : {}),
        })
      )
        finish(true);
      else
        setError(
          'Could not save. Your payment and status must be confirmed together. Try again or cancel.',
        );
    } catch (error) {
      if (error instanceof AdminApiError && error.status === 409) {
        current.current = null;
        setPending(null);
        value.reject(error);
      }
      setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };
  const close = () => {
    if (saving || !pending) return;
    const dirty =
      amount !== formatCrmMoneyInput(pending.remaining) ||
      galleryUrl !== (pending.draft.galleryUrl ?? '');
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    finish(false);
  };
  const dialog = (
    <Dialog.Root
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="shoot-panel__overlay" />
        <Dialog.Content
          ref={capturePanel}
          inert={pending === null}
          className="shoot-panel shoot-money-form dui-compact-panel"
          data-compact-panel
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            const selector = window.matchMedia('(max-width:1023px)').matches
              ? '[aria-label="Close remaining payment"]'
              : 'input';
            content.current?.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(event) => {
            if (returnFocus.current?.isConnected) {
              event.preventDefault();
              returnFocus.current.focus({ preventScroll: true });
            }
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            if (saving) e.preventDefault();
          }}
        >
          <div className="shoot-panel__heading" data-panel-header>
            <Dialog.Title>
              {displayPending?.draft.status === 'Delivered' ? 'Deliver shoot' : 'Remaining payment'}
            </Dialog.Title>
            <button
              className="shoot-button shoot-button--icon"
              aria-label="Close remaining payment"
              disabled={saving}
              onClick={close}
            >
              <X size={20} />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit((displayPending?.remaining ?? 0) > 0);
            }}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                e.stopPropagation();
                void submit((displayPending?.remaining ?? 0) > 0);
              }
            }}
          >
            <div ref={bodyRef} data-panel-body>
              <Dialog.Description>
                {displayPending && displayPending.remaining > 0
                  ? `${formatCrmMoney(displayPending.remaining, displayPending.draft.currency, true)} unpaid. Add a payment or continue.`
                  : displayPending?.draft.status === 'Delivered'
                    ? 'Add the finished gallery link.'
                    : ''}
              </Dialog.Description>
              {displayPending?.draft.status === 'Delivered' ? (
                <label>
                  Gallery link
                  <input
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    value={galleryUrl}
                    disabled={saving}
                    placeholder="https://…"
                    onChange={(e) => setGalleryUrl(e.target.value)}
                  />
                </label>
              ) : null}
              {(displayPending?.remaining ?? 0) > 0 ? (
                <label>
                  Payment amount ({displayPending?.draft.currency})
                  <input
                    inputMode="decimal"
                    value={amount}
                    disabled={saving}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </label>
              ) : null}
            </div>
            <footer data-panel-footer>
              {error ? <p role="alert">{error}</p> : null}
              <div className="shoot-money__actions">
                {(displayPending?.remaining ?? 0) > 0 ? (
                  <button
                    type="button"
                    className="shoot-button shoot-button--outline"
                    disabled={saving}
                    onClick={() => void submit(false)}
                  >
                    Skip payment
                  </button>
                ) : null}
                <button
                  className="shoot-button shoot-button--primary"
                  aria-busy={saving}
                  disabled={saving}
                >
                  {saving
                    ? 'Saving…'
                    : displayPending?.draft.status === 'Delivered'
                      ? 'Deliver shoot'
                      : 'Save & continue'}
                </button>
              </div>
            </footer>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
  return { request, dialog, cancel: () => finish(false) };
}
